import React, { useState, useEffect, useRef, Fragment } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  addDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
  runTransaction,
} from "firebase/firestore";
import {
  XMarkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  BanknotesIcon,
  ClockIcon,
  ChartBarIcon,
  PrinterIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  CommandLineIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ShoppingCartIcon,
  ReceiptPercentIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolid } from "@heroicons/react/24/solid";
import Toast from "./Toast";
import Button from "./UI/Button";
import Card from "./UI/Card";
import Input from "./UI/Input";
import Badge from "./UI/Badge";
import Modal from "./UI/Modal";

const CashierView = () => {
  // State management
  const [pendingOrders, setPendingOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editedOrder, setEditedOrder] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentSettings, setPaymentSettings] = useState({
    enabled: false,
    accountNumber: "",
    viber: "",
    qrCodeUrl: "",
  });
  const [toast, setToast] = useState(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [newOrder, setNewOrder] = useState({ items: [] });
  const [showNewOrderProducts, setShowNewOrderProducts] = useState(false);
  const [showEndOfDay, setShowEndOfDay] = useState(false);
  const [todaySales, setTodaySales] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [showStartSession, setShowStartSession] = useState(false);
  const [openingCash, setOpeningCash] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [cashReceived, setCashReceived] = useState("");
  const [orderToConfirm, setOrderToConfirm] = useState(null);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [paymentType, setPaymentType] = useState("cash");
  const [isNewOrderPayment, setIsNewOrderPayment] = useState(false);
  const [denominations, setDenominations] = useState({
    note1000: 0,
    note500: 0,
    note100: 0,
    note50: 0,
    note20: 0,
    note10: 0,
    note5: 0,
    coins: 0,
  });
  const [showSessionSelector, setShowSessionSelector] = useState(false);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);

  const previousOrderCount = useRef(0);
  const isFirstLoad = useRef(true);

  const GST_RATE = 0.08;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const showPaymentInfoToUser = async () => {
    // Trigger modal in user view via Firestore
    try {
      const uiStateRef = doc(db, "ui_state", "payment_modal");
      await setDoc(uiStateRef, { show: true, timestamp: new Date().toISOString() });

      showToast("Payment info sent to customer display", "success");

      // Auto-hide after modal is opened (users can close it manually)
      setTimeout(async () => {
        await setDoc(uiStateRef, { show: false });
      }, 5000);
    } catch (error) {
      console.error("Error triggering payment modal:", error);
      showToast("Failed to send payment info", "error");
    }
  };

  // Function to play notification sound
  const playNotificationSound = () => {
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = "sine";

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContext.currentTime + 0.5
    );

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);

    setTimeout(() => {
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();

      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);

      oscillator2.frequency.value = 600;
      oscillator2.type = "sine";

      gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode2.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.5
      );

      oscillator2.start(audioContext.currentTime);
      oscillator2.stop(audioContext.currentTime + 0.5);
    }, 200);
  };

  useEffect(() => {
    fetchProducts();
    fetchPaymentSettings();
    checkActiveSession();

    // Check for pending End of Day session from SessionsView
    const pendingSession = localStorage.getItem("pendingEndOfDaySession");
    if (pendingSession) {
      try {
        const session = JSON.parse(pendingSession);
        setActiveSession(session);
        localStorage.removeItem("pendingEndOfDaySession");

        setTimeout(() => {
          openEndOfDay();
        }, 500);
      } catch (error) {
        console.error("Error loading pending session:", error);
        localStorage.removeItem("pendingEndOfDaySession");
      }
    }

    // Set up real-time listener for pending orders
    const ordersQuery = query(
      collection(db, "orders"),
      where("status", "==", "pending")
    );

    const unsubscribe = onSnapshot(
      ordersQuery,
      (snapshot) => {
        const ordersList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setPendingOrders(ordersList);
        setLoading(false);

        // Play sound if new order arrived
        if (
          !isFirstLoad.current &&
          ordersList.length > previousOrderCount.current
        ) {
          console.log("New order detected! Playing sound...");
          playNotificationSound();

          if (
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            new Notification("New Order!", {
              body: "A new order has been placed.",
              icon: "/favicon.ico",
            });
          }
        }

        previousOrderCount.current = ordersList.length;
        isFirstLoad.current = false;
      },
      (error) => {
        console.error("Error listening for orders:", error);
        setLoading(false);
      }
    );

    // Request notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + N: Create New Order
      if (modKey && e.key === "n") {
        e.preventDefault();
        if (activeSession && !isCreatingOrder) {
          startCreatingOrder();
          showToast("New order started (Ctrl+N)", "info");
        }
      }

      // Ctrl/Cmd + S: Start Session
      if (modKey && e.key === "s") {
        e.preventDefault();
        if (!activeSession) {
          setShowStartSession(true);
          showToast("Opening Start Session (Ctrl+S)", "info");
        }
      }

      // Ctrl/Cmd + E: End of Day
      if (modKey && e.key === "e") {
        e.preventDefault();
        if (activeSession) {
          openEndOfDay();
          showToast("Opening End of Day (Ctrl+E)", "info");
        }
      }

      // C: Confirm Order
      if (e.key === "c" && selectedOrder && !isCreatingOrder && !isEditing) {
        e.preventDefault();
        initiatePayment(selectedOrder);
        showToast("Processing payment (C)", "info");
      }

      // E: Edit Order
      if (
        e.key === "e" &&
        selectedOrder &&
        !isCreatingOrder &&
        !isEditing &&
        !modKey
      ) {
        e.preventDefault();
        startEditing();
        showToast("Edit mode enabled (E)", "info");
      }

      // Ctrl/Cmd + /: Show shortcuts
      if (modKey && e.key === "/") {
        e.preventDefault();
        setShowShortcutsHelp(true);
      }

      // Esc: Cancel/Close
      if (e.key === "Escape") {
        if (showPaymentModal) setShowPaymentModal(false);
        else if (showShortcutsHelp) setShowShortcutsHelp(false);
        else if (isEditing) cancelEditing();
        else if (isCreatingOrder) cancelNewOrder();
      }

      // Arrow keys for navigation
      if (!isEditing && !isCreatingOrder && pendingOrders.length > 0) {
        const currentIndex = pendingOrders.findIndex(
          (o) => o.id === selectedOrder?.id
        );

        if (e.key === "ArrowDown") {
          e.preventDefault();
          const nextIndex =
            currentIndex < pendingOrders.length - 1 ? currentIndex + 1 : 0;
          handleOrderClick(pendingOrders[nextIndex]);
        }

        if (e.key === "ArrowUp") {
          e.preventDefault();
          const prevIndex =
            currentIndex > 0 ? currentIndex - 1 : pendingOrders.length - 1;
          handleOrderClick(pendingOrders[prevIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSession,
    isCreatingOrder,
    selectedOrder,
    isEditing,
    pendingOrders,
    showPaymentModal,
    showShortcutsHelp,
  ]);

  const fetchProducts = async () => {
    try {
      const productsRef = collection(db, "products");
      const productsSnapshot = await getDocs(productsRef);
      const productsList = productsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productsList);
    } catch (error) {
      console.error("Error fetching products:", error);
      showToast("Failed to load products", "error");
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const settingsRef = collection(db, "settings");
      const settingsSnapshot = await getDocs(settingsRef);
      const settings = settingsSnapshot.docs.find(
        (doc) => doc.id === "payment"
      );
      if (settings) {
        setPaymentSettings(settings.data());
      }
    } catch (error) {
      console.error("Error fetching payment settings:", error);
    }
  };

  const checkActiveSession = async () => {
    try {
      const sessionsQuery = query(
        collection(db, "sessions"),
        where("status", "==", "active")
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      if (sessionsSnapshot.empty) {
        setActiveSession(null);
        return;
      }

      const activeSessions = sessionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (activeSessions.length >= 1) {
        setActiveSession(activeSessions[0]);
        if (activeSessions.length > 1) {
          showToast("Active session found", "info");
        }
      }
    } catch (error) {
      console.error("Error checking active session:", error);
    }
  };

  const startSession = async () => {
    if (!openingCash || parseFloat(openingCash) < 0) {
      showToast("Please enter a valid opening cash amount", "error");
      return;
    }

    try {
      const sessionData = {
        startTime: new Date().toISOString(),
        startedBy: localStorage.getItem("username") || "Cashier",
        openingCash: parseFloat(openingCash),
        status: "active",
        date: new Date().toLocaleDateString(),
      };

      const sessionRef = await addDoc(collection(db, "sessions"), sessionData);
      setActiveSession({ id: sessionRef.id, ...sessionData });
      setShowStartSession(false);
      setOpeningCash("");
      showToast("Session started successfully!", "success");
    } catch (error) {
      console.error("Error starting session:", error);
      showToast("Failed to start session", "error");
    }
  };

  const openEndOfDay = async () => {
    try {
      const sessionsQuery = query(
        collection(db, "sessions"),
        where("status", "==", "active")
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      if (sessionsSnapshot.empty) {
        showToast(
          "No active session found. Please start a session first.",
          "warning"
        );
        return;
      }

      const activeSessions = sessionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (activeSessions.length === 1) {
        setActiveSession(activeSessions[0]);
        setShowEndOfDay(true);
        setDenominations({
          note1000: 0,
          note500: 0,
          note100: 0,
          note50: 0,
          note20: 0,
          note10: 0,
          note5: 0,
          coins: 0,
        });
        await fetchTodaySales();
      } else {
        setAvailableSessions(activeSessions);
        setShowSessionSelector(true);
      }
    } catch (error) {
      console.error("Error checking active sessions:", error);
      showToast("Failed to check active sessions", "error");
    }
  };

  const selectSessionForEndOfDay = async (session) => {
    setActiveSession(session);
    setShowSessionSelector(false);
    setShowEndOfDay(true);
    setDenominations({
      note1000: 0,
      note500: 0,
      note100: 0,
      note50: 0,
      note20: 0,
      note10: 0,
      note5: 0,
      coins: 0,
    });
    await fetchTodaySales();
  };

  const closeEndOfDay = () => {
    setShowEndOfDay(false);
    setTodaySales([]);
    setDenominations({
      note1000: 0,
      note500: 0,
      note100: 0,
      note50: 0,
      note20: 0,
      note10: 0,
      note5: 0,
      coins: 0,
    });
  };

  const fetchTodaySales = async () => {
    setLoadingSales(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const salesCollection = collection(db, "sales");
      const salesSnapshot = await getDocs(salesCollection);

      const salesList = salesSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((sale) => {
          const saleDate = new Date(sale.date);
          saleDate.setHours(0, 0, 0, 0);
          return saleDate.toISOString() === todayISO;
        });

      setTodaySales(salesList);
    } catch (error) {
      console.error("Error fetching today sales:", error);
      showToast("Failed to load sales data", "error");
    } finally {
      setLoadingSales(false);
    }
  };

  const calculateDaySummary = () => {
    const totalSales = todaySales.reduce(
      (sum, sale) => sum + (sale.totalWithGST || 0),
      0
    );
    const totalOrders = todaySales.length;
    const totalItems = todaySales.reduce(
      (sum, sale) =>
        sum + sale.items.reduce((itemSum, item) => itemSum + item.quantity, 0),
      0
    );
    const subtotal = todaySales.reduce(
      (sum, sale) => sum + (sale.subtotal || 0),
      0
    );
    const gst = todaySales.reduce((sum, sale) => sum + (sale.gst || 0), 0);
    const cashSales = todaySales
      .filter((sale) => sale.paymentType === "cash")
      .reduce((sum, sale) => sum + (sale.totalWithGST || 0), 0);
    const bankSales = todaySales
      .filter((sale) => sale.paymentType === "bank")
      .reduce((sum, sale) => sum + (sale.totalWithGST || 0), 0);

    return {
      totalSales,
      totalOrders,
      totalItems,
      subtotal,
      gst,
      cashSales,
      bankSales,
    };
  };

  const calculateTotalFromDenominations = () => {
    return (
      denominations.note1000 * 1000 +
      denominations.note500 * 500 +
      denominations.note100 * 100 +
      denominations.note50 * 50 +
      denominations.note20 * 20 +
      denominations.note10 * 10 +
      denominations.note5 * 5 +
      parseFloat(denominations.coins || 0)
    );
  };

  const calculateExpectedCash = () => {
    if (!activeSession) return 0;
    const { cashSales } = calculateDaySummary();
    return activeSession.openingCash + cashSales;
  };

  const handleDenominationChange = (denomination, value) => {
    setDenominations((prev) => ({
      ...prev,
      [denomination]: parseInt(value) || 0,
    }));
  };

  const handleCoinsChange = (value) => {
    setDenominations((prev) => ({
      ...prev,
      coins: parseFloat(value) || 0,
    }));
  };

  const archiveDay = async () => {
    if (!activeSession) {
      showToast("No active session found", "error");
      return;
    }

    try {
      const summary = calculateDaySummary();
      const closingCashAmount = calculateTotalFromDenominations();
      const expectedCash = calculateExpectedCash();
      const difference = closingCashAmount - expectedCash;

      // Update session to closed
      const sessionRef = doc(db, "sessions", activeSession.id);
      await updateDoc(sessionRef, {
        status: "closed",
        endTime: new Date().toISOString(),
        closingCash: closingCashAmount,
        denominations: denominations,
        totalSales: summary.totalSales,
        totalOrders: summary.totalOrders,
        cashSales: summary.cashSales,
        bankSales: summary.bankSales,
        difference: difference,
        expectedCash: expectedCash,
      });

      showToast("Session closed successfully!", "success");
      setActiveSession(null);
      closeEndOfDay();
      checkActiveSession();
    } catch (error) {
      console.error("Error closing session:", error);
      showToast("Failed to close session", "error");
    }
  };

  const exportToCSV = () => {
    if (todaySales.length === 0) {
      showToast("No sales data to export", "warning");
      return;
    }

    const summary = calculateDaySummary();
    const date = new Date().toLocaleDateString();

    let csv = "End of Day Report\n";
    csv += `Date: ${date}\n\n`;
    csv += "Summary\n";
    csv += `Total Orders,${summary.totalOrders}\n`;
    csv += `Total Items Sold,${summary.totalItems}\n`;
    csv += `Subtotal,MVR ${summary.subtotal.toFixed(2)}\n`;
    csv += `GST (8%),MVR ${summary.gst.toFixed(2)}\n`;
    csv += `Total Sales,MVR ${summary.totalSales.toFixed(2)}\n\n`;

    csv += "Order Details\n";
    csv += "Order ID,Time,Items,Subtotal,GST,Total\n";

    todaySales.forEach((sale) => {
      const time = new Date(sale.date).toLocaleTimeString();
      const itemsList = sale.items
        .map((item) => `${item.name} x${item.quantity}`)
        .join("; ");
      csv += `${sale.orderId.substring(
        0,
        8
      )},${time},"${itemsList}",${sale.subtotal.toFixed(2)},${sale.gst.toFixed(
        2
      )},${sale.totalWithGST.toFixed(2)}\n`;
    });

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `end-of-day-${date.replace(/\//g, "-")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    showToast("Report exported successfully!", "success");
  };

  const printReport = () => {
    if (todaySales.length === 0) {
      showToast("No sales data to print", "warning");
      return;
    }

    const summary = calculateDaySummary();
    const date = new Date().toLocaleDateString();
    const time = new Date().toLocaleTimeString();

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>End of Day Report - ${date}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; color: #2c3e50; }
            .summary { background: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0; }
            .summary-row { display: flex; justify-content: space-between; padding: 5px 0; }
            .summary-row.total { font-weight: bold; font-size: 1.2em; border-top: 2px solid #333; padding-top: 10px; margin-top: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background: #667eea; color: white; }
            .footer { margin-top: 30px; text-align: center; color: #7f8c8d; }
          </style>
        </head>
        <body>
          <h1>End of Day Report</h1>
          <p style="text-align: center; color: #7f8c8d;">Date: ${date} | Time: ${time}</p>

          <div class="summary">
            <h2>Sales Summary</h2>
            <div class="summary-row"><span>Total Orders:</span><span>${
              summary.totalOrders
            }</span></div>
            <div class="summary-row"><span>Total Items Sold:</span><span>${
              summary.totalItems
            }</span></div>
            <div class="summary-row"><span>Subtotal:</span><span>MVR ${summary.subtotal.toFixed(
              2
            )}</span></div>
            <div class="summary-row"><span>GST (8%):</span><span>MVR ${summary.gst.toFixed(
              2
            )}</span></div>
            <div class="summary-row total"><span>Total Sales:</span><span>MVR ${summary.totalSales.toFixed(
              2
            )}</span></div>
          </div>

          <h2>Order Details</h2>
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Time</th>
                <th>Items</th>
                <th>Subtotal</th>
                <th>GST</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${todaySales
                .map(
                  (sale) => `
                <tr>
                  <td>${sale.orderId.substring(0, 8)}</td>
                  <td>${new Date(sale.date).toLocaleTimeString()}</td>
                  <td>${sale.items
                    .map((item) => `${item.name} x${item.quantity}`)
                    .join(", ")}</td>
                  <td>MVR ${sale.subtotal.toFixed(2)}</td>
                  <td>MVR ${sale.gst.toFixed(2)}</td>
                  <td>MVR ${sale.totalWithGST.toFixed(2)}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>

          <div class="footer">
            <p>Generated on ${date} at ${time}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setIsEditing(false);
    setEditedOrder(null);
  };

  const startCreatingOrder = () => {
    setIsCreatingOrder(true);
    setSelectedOrder(null);
    setNewOrder({ items: [] });
    setShowNewOrderProducts(false);
  };

  const cancelNewOrder = () => {
    setIsCreatingOrder(false);
    setNewOrder({ items: [] });
    setShowNewOrderProducts(false);
  };

  const addProductToNewOrder = (product) => {
    const existingItem = newOrder.items.find((item) => item.id === product.id);

    if (existingItem) {
      // Check stock limit
      if (
        product.inventoryEnabled &&
        existingItem.quantity >= product.stockQuantity
      ) {
        showToast(
          `Only ${product.stockQuantity} available in stock`,
          "warning"
        );
        return;
      }

      setNewOrder((prev) => ({
        items: prev.items.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        ),
      }));
    } else {
      setNewOrder((prev) => ({
        items: [
          ...prev.items,
          {
            id: product.id,
            name: product.name,
            price: product.price,
            quantity: 1,
            inventoryEnabled: product.inventoryEnabled || false,
            stockQuantity: product.stockQuantity || 0,
          },
        ],
      }));
    }

    showToast(`${product.name} added to order`, "success");
  };

  const updateNewOrderItemQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItemFromNewOrder(productId);
      return;
    }

    const item = newOrder.items.find((i) => i.id === productId);
    if (item && item.inventoryEnabled && newQuantity > item.stockQuantity) {
      showToast(`Only ${item.stockQuantity} available in stock`, "warning");
      return;
    }

    setNewOrder((prev) => ({
      items: prev.items.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      ),
    }));
  };

  const removeItemFromNewOrder = (productId) => {
    setNewOrder((prev) => ({
      items: prev.items.filter((item) => item.id !== productId),
    }));
  };

  const submitNewOrder = async () => {
    if (newOrder.items.length === 0) {
      showToast("Please add at least one item to the order", "warning");
      return;
    }

    setOrderToConfirm({
      items: newOrder.items,
      totalAmount: calculateTotalWithGST(calculateOrderTotal(newOrder.items)),
    });
    setIsNewOrderPayment(true);
    setShowPaymentModal(true);
  };

  const startEditing = () => {
    setIsEditing(true);
    setEditedOrder({
      ...selectedOrder,
      items: [...selectedOrder.items],
    });
    setShowProductSelector(false);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedOrder(null);
    setShowProductSelector(false);
  };

  const addProductToEditedOrder = (product) => {
    const existingItem = editedOrder.items.find(
      (item) => item.id === product.id
    );

    if (existingItem) {
      if (
        product.inventoryEnabled &&
        existingItem.quantity >= product.stockQuantity
      ) {
        showToast(
          `Only ${product.stockQuantity} available in stock`,
          "warning"
        );
        return;
      }

      setEditedOrder((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        ),
      }));
    } else {
      setEditedOrder((prev) => ({
        ...prev,
        items: [
          ...prev.items,
          {
            id: product.id,
            name: product.name,
            price: product.price,
            quantity: 1,
          },
        ],
      }));
    }

    showToast(`${product.name} added`, "success");
  };

  const updateEditedItemQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeEditedItem(productId);
      return;
    }

    setEditedOrder((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      ),
    }));
  };

  const removeEditedItem = (productId) => {
    setEditedOrder((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== productId),
    }));
  };

  const saveEditedOrder = async () => {
    if (editedOrder.items.length === 0) {
      showToast("Order must have at least one item", "warning");
      return;
    }

    try {
      const newTotal = calculateTotalWithGST(
        calculateOrderTotal(editedOrder.items)
      );
      const orderRef = doc(db, "orders", selectedOrder.id);

      await updateDoc(orderRef, {
        items: editedOrder.items,
        totalAmount: newTotal,
      });

      setSelectedOrder({
        ...selectedOrder,
        items: editedOrder.items,
        totalAmount: newTotal,
      });

      setIsEditing(false);
      setEditedOrder(null);
      showToast("Order updated successfully!", "success");
    } catch (error) {
      console.error("Error updating order:", error);
      showToast("Failed to update order", "error");
    }
  };

  const initiatePayment = (order) => {
    setOrderToConfirm(order);
    setIsNewOrderPayment(false);
    setShowPaymentModal(true);
    setCashReceived("");
    setPaymentType("cash");
  };

  const confirmOrder = async () => {
    if (!orderToConfirm) return;

    if (
      paymentType === "cash" &&
      (!cashReceived || parseFloat(cashReceived) < orderToConfirm.totalAmount)
    ) {
      showToast("Insufficient cash received", "error");
      return;
    }

    try {
      const saleData = {
        orderId: isNewOrderPayment ? `ORD-${Date.now()}` : orderToConfirm.id,
        items: orderToConfirm.items,
        subtotal: calculateOrderTotal(orderToConfirm.items),
        gst: calculateGST(orderToConfirm.totalAmount),
        totalWithGST: orderToConfirm.totalAmount,
        date: new Date().toISOString(),
        paymentType: paymentType,
        cashReceived: paymentType === "cash" ? parseFloat(cashReceived) : null,
        change:
          paymentType === "cash"
            ? parseFloat(cashReceived) - orderToConfirm.totalAmount
            : null,
        confirmedBy: localStorage.getItem("username") || "Cashier",
        sessionId: activeSession?.id || null,
      };

      // Add sale to database
      await addDoc(collection(db, "sales"), saleData);

      // Update inventory for products with inventory tracking
      for (const item of orderToConfirm.items) {
        const product = products.find((p) => p.id === item.id);
        if (product && product.inventoryEnabled) {
          const productRef = doc(db, "products", item.id);
          await runTransaction(db, async (transaction) => {
            const productDoc = await transaction.get(productRef);
            if (productDoc.exists()) {
              const currentStock = productDoc.data().stockQuantity || 0;
              const newStock = currentStock - item.quantity;
              transaction.update(productRef, {
                stockQuantity: Math.max(0, newStock),
              });

              // Add inventory transaction
              await addDoc(collection(db, "inventory_transactions"), {
                productId: item.id,
                productName: item.name,
                type: "subtract",
                quantity: item.quantity,
                reason: `Order ${saleData.orderId} confirmed`,
                timestamp: serverTimestamp(),
                performedBy: localStorage.getItem("username") || "Cashier",
                previousStock: currentStock,
                newStock: Math.max(0, newStock),
              });
            }
          });
        }
      }

      // Update or delete order
      if (!isNewOrderPayment) {
        const orderRef = doc(db, "orders", orderToConfirm.id);
        await updateDoc(orderRef, { status: "confirmed" });
      }

      // Print bill
      printBill(saleData);

      // Reset state
      setShowPaymentModal(false);
      setOrderToConfirm(null);
      setCashReceived("");
      setPaymentType("cash");
      setSelectedOrder(null);
      setIsCreatingOrder(false);
      setNewOrder({ items: [] });

      // Refresh products
      await fetchProducts();

      showToast(
        paymentType === "cash"
          ? `Order confirmed! Change: MVR ${(
              parseFloat(cashReceived) - orderToConfirm.totalAmount
            ).toFixed(2)}`
          : "Order confirmed!",
        "success"
      );
    } catch (error) {
      console.error("Error confirming order:", error);
      showToast("Failed to confirm order", "error");
    }
  };

  const printBill = (sale) => {
    const printWindow = window.open("", "_blank");
    const date = new Date(sale.date);

    printWindow.document.write(`
      <html>
        <head>
          <title>Bill - ${sale.orderId}</title>
          <style>
            body {
              font-family: 'Courier New', monospace;
              max-width: 300px;
              margin: 0 auto;
              padding: 20px;
            }
            h2 { text-align: center; margin: 10px 0; }
            .info { text-align: center; margin: 5px 0; font-size: 12px; }
            .divider { border-top: 1px dashed #000; margin: 10px 0; }
            .items { margin: 10px 0; }
            .item { display: flex; justify-content: space-between; margin: 5px 0; }
            .summary { margin-top: 10px; }
            .summary-row { display: flex; justify-content: space-between; margin: 3px 0; }
            .total { font-weight: bold; font-size: 1.2em; border-top: 1px solid #000; padding-top: 5px; margin-top: 5px; }
            .footer { text-align: center; margin-top: 20px; font-size: 11px; }
          </style>
        </head>
        <body>
          <h2>RECEIPT</h2>
          <div class="info">Order ID: ${sale.orderId.substring(0, 12)}</div>
          <div class="info">${date.toLocaleDateString()} ${date.toLocaleTimeString()}</div>
          <div class="info">Cashier: ${sale.confirmedBy || "Cashier"}</div>

          <div class="divider"></div>

          <div class="items">
            ${sale.items
              .map(
                (item) => `
              <div class="item">
                <span>${item.name} x${item.quantity}</span>
                <span>MVR ${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            `
              )
              .join("")}
          </div>

          <div class="divider"></div>

          <div class="summary">
            <div class="summary-row">
              <span>Subtotal:</span>
              <span>MVR ${sale.subtotal.toFixed(2)}</span>
            </div>
            <div class="summary-row">
              <span>GST (8%):</span>
              <span>MVR ${sale.gst.toFixed(2)}</span>
            </div>
            <div class="summary-row total">
              <span>TOTAL:</span>
              <span>MVR ${sale.totalWithGST.toFixed(2)}</span>
            </div>

            ${
              sale.paymentType === "cash" && sale.cashReceived
                ? `
              <div class="summary-row" style="margin-top: 10px;">
                <span>Cash Received:</span>
                <span>MVR ${sale.cashReceived.toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span>Change:</span>
                <span>MVR ${sale.change.toFixed(2)}</span>
              </div>
            `
                : ""
            }
          </div>

          <div class="divider"></div>

          <div class="footer">
            <p>Thank you for your purchase!</p>
            <p>Please come again</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const returnOrderToUser = async (orderId) => {
    try {
      const orderRef = doc(db, "orders", orderId);
      await updateDoc(orderRef, { status: "pending" });
      setSelectedOrder(null);
      showToast("Order returned to user", "success");
    } catch (error) {
      console.error("Error returning order:", error);
      showToast("Failed to return order", "error");
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) {
      return;
    }

    try {
      const orderRef = doc(db, "orders", orderId);
      await updateDoc(orderRef, { status: "cancelled" });
      setSelectedOrder(null);
      showToast("Order deleted", "success");
    } catch (error) {
      console.error("Error deleting order:", error);
      showToast("Failed to delete order", "error");
    }
  };

  const calculateOrderTotal = (items) => {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const calculateGST = (total) => {
    return (total * GST_RATE) / (1 + GST_RATE);
  };

  const calculateBasePrice = (totalWithGST) => {
    return totalWithGST / (1 + GST_RATE);
  };

  const calculateTotalWithGST = (subtotal) => {
    return subtotal * (1 + GST_RATE);
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Session Status Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Cashier Dashboard
            </h1>
            <p className="text-text-secondary">
              Manage pending orders and process payments
            </p>
          </div>

          {activeSession ? (
            <Card className="bg-success-50 border-success-500">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 bg-success-500 rounded-full animate-pulse"></div>
                <div>
                  <p className="text-sm font-medium text-success-900">
                    Active Session
                  </p>
                  <p className="text-xs text-success-700">
                    Started:{" "}
                    {new Date(activeSession.startTime).toLocaleTimeString()}
                  </p>
                  <p className="text-xs text-success-700">
                    Opening Cash: MVR {activeSession.openingCash.toFixed(2)}
                  </p>
                </div>
              </div>
            </Card>
          ) : (
            <Button
              variant="primary"
              size="lg"
              onClick={() => setShowStartSession(true)}
              leftIcon={<ClockIcon className="h-5 w-5" />}
            >
              Start Session
            </Button>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mt-4">
          {activeSession && (
            <>
              <Button
                variant="success"
                onClick={startCreatingOrder}
                disabled={isCreatingOrder}
                leftIcon={<PlusIcon className="h-5 w-5" />}
              >
                New Order
              </Button>
              <Button
                variant="warning"
                onClick={openEndOfDay}
                leftIcon={<ChartBarIcon className="h-5 w-5" />}
              >
                End of Day
              </Button>
            </>
          )}
          <Button
            variant="outline"
            onClick={() => setShowShortcutsHelp(true)}
            leftIcon={<CommandLineIcon className="h-5 w-5" />}
          >
            Shortcuts
          </Button>
          {paymentSettings.enabled && (
            <Button
              variant="info"
              onClick={showPaymentInfoToUser}
              leftIcon={<BanknotesIcon className="h-5 w-5" />}
            >
              Payment Info
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Orders List */}
        <Card className="lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
              <ShoppingCartIcon className="h-6 w-6 text-primary-600" />
              Pending Orders
            </h2>
            <Badge variant={pendingOrders.length > 0 ? "warning" : "neutral"}>
              {pendingOrders.length}
            </Badge>
          </div>

          <div className="space-y-2 max-h-[calc(100vh-20rem)] overflow-y-auto">
            {pendingOrders.length === 0 ? (
              <div className="text-center py-12 text-text-secondary">
                <ShoppingCartIcon className="h-16 w-16 mx-auto mb-4 opacity-20" />
                <p>No pending orders</p>
                <p className="text-sm mt-2">New orders will appear here</p>
              </div>
            ) : (
              pendingOrders.map((order) => (
                <button
                  key={order.id}
                  onClick={() => handleOrderClick(order)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    selectedOrder?.id === order.id
                      ? "border-primary-500 bg-primary-50 shadow-primary"
                      : "border-neutral-200 hover:border-primary-300 hover:bg-neutral-50"
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-text-primary">
                        Order #{order.orderNumber || order.id.substring(0, 8)}
                      </p>
                      <p className="text-xs text-text-secondary mt-1">
                        {order.timestamp ? new Date(order.timestamp).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                    <Badge variant="warning">Pending</Badge>
                  </div>

                  <div className="text-sm text-text-secondary mb-2">
                    {order.items.length} item
                    {order.items.length !== 1 ? "s" : ""}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-text-secondary">
                      Total:
                    </span>
                    <span className="text-lg font-bold text-primary-600">
                      MVR {order.totalAmount.toFixed(2)}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>

        {/* Order Details Panel */}
        <Card className="lg:col-span-2">
          {isCreatingOrder ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-text-primary">
                  Create New Order
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelNewOrder}
                  leftIcon={<XMarkIcon className="h-5 w-5" />}
                >
                  Cancel
                </Button>
              </div>

              {/* Product Selector Toggle */}
              <div className="mb-6">
                <Button
                  variant={showNewOrderProducts ? "outline" : "primary"}
                  onClick={() => setShowNewOrderProducts(!showNewOrderProducts)}
                  leftIcon={
                    showNewOrderProducts ? (
                      <XMarkIcon className="h-5 w-5" />
                    ) : (
                      <PlusIcon className="h-5 w-5" />
                    )
                  }
                  fullWidth
                >
                  {showNewOrderProducts ? "Hide Products" : "Add Product"}
                </Button>
              </div>

              {/* Product Selector */}
              {showNewOrderProducts && (
                <Card className="mb-6 bg-neutral-50">
                  <div className="mb-4">
                    <Input
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      leftIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                    {filteredProducts.length === 0 ? (
                      <p className="col-span-2 text-center text-text-secondary py-8">
                        No products found
                      </p>
                    ) : (
                      filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-neutral-200 hover:border-primary-300 transition-colors"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-text-primary">
                              {product.name}
                            </p>
                            <p className="text-sm text-text-secondary">
                              MVR {product.price.toFixed(2)}
                            </p>
                            {product.inventoryEnabled && (
                              <div className="mt-1">
                                {product.stockQuantity === 0 ? (
                                  <Badge variant="danger" size="sm">
                                    Out of Stock
                                  </Badge>
                                ) : product.stockQuantity <=
                                  product.lowStockThreshold ? (
                                  <Badge variant="warning" size="sm">
                                    Low: {product.stockQuantity}
                                  </Badge>
                                ) : (
                                  <Badge variant="success" size="sm">
                                    Stock: {product.stockQuantity}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => addProductToNewOrder(product)}
                            disabled={
                              product.inventoryEnabled &&
                              product.stockQuantity === 0
                            }
                          >
                            Add
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              )}

              {/* Order Items List */}
              <div className="mb-6">
                <h3 className="font-bold text-text-primary mb-3 flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5" />
                  Order Items
                </h3>

                {newOrder.items.length === 0 ? (
                  <div className="text-center py-12 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-300">
                    <ShoppingCartIcon className="h-16 w-16 mx-auto mb-4 text-neutral-300" />
                    <p className="text-text-secondary">No items in order</p>
                    <p className="text-sm text-text-secondary mt-2">
                      Add products above
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {newOrder.items.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-text-primary">
                            {item.name}
                          </p>
                          <p className="text-sm text-text-secondary">
                            MVR {item.price.toFixed(2)} each
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateNewOrderItemQuantity(
                                item.id,
                                item.quantity - 1
                              )
                            }
                          >
                            -
                          </Button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              updateNewOrderItemQuantity(
                                item.id,
                                parseInt(e.target.value) || 0
                              )
                            }
                            className="w-16 px-2 py-1 text-center border border-neutral-300 rounded-md"
                            min="1"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateNewOrderItemQuantity(
                                item.id,
                                item.quantity + 1
                              )
                            }
                          >
                            +
                          </Button>
                          <span className="text-lg font-bold text-primary-600 w-24 text-right">
                            MVR {(item.price * item.quantity).toFixed(2)}
                          </span>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => removeItemFromNewOrder(item.id)}
                            leftIcon={<TrashIcon className="h-4 w-4" />}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Order Summary */}
              <Card className="bg-gradient-to-br from-primary-50 to-purple-50 border-primary-200">
                <h3 className="font-bold text-text-primary mb-4">
                  Order Summary
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-text-secondary">
                    <span>Subtotal:</span>
                    <span>
                      MVR {calculateOrderTotal(newOrder.items).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-text-secondary">
                    <span>GST (8%):</span>
                    <span>
                      MVR{" "}
                      {calculateGST(
                        calculateOrderTotal(newOrder.items)
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="h-px bg-primary-200 my-2"></div>
                  <div className="flex justify-between text-xl font-bold text-primary-600">
                    <span>Total with GST:</span>
                    <span>
                      MVR{" "}
                      {calculateTotalWithGST(
                        calculateOrderTotal(newOrder.items)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <Button
                    variant="success"
                    size="lg"
                    onClick={submitNewOrder}
                    disabled={newOrder.items.length === 0}
                    fullWidth
                    leftIcon={<CheckCircleIcon className="h-5 w-5" />}
                  >
                    Create & Confirm Order
                  </Button>
                </div>
              </Card>
            </div>
          ) : selectedOrder ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-text-primary">
                  Order #{selectedOrder.orderNumber || selectedOrder.id.substring(0, 8)}
                </h2>
                <Badge variant="warning">Pending</Badge>
              </div>

              {!isEditing ? (
                <>
                  {/* Order Items Display */}
                  <div className="mb-6">
                    <h3 className="font-bold text-text-primary mb-3 flex items-center gap-2">
                      <DocumentTextIcon className="h-5 w-5" />
                      Items
                    </h3>
                    <div className="space-y-2">
                      {selectedOrder.items.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-text-primary">
                              {item.name}
                            </p>
                            <p className="text-sm text-text-secondary">
                              x{item.quantity}
                            </p>
                          </div>
                          <span className="text-lg font-bold text-primary-600">
                            MVR {(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Order Summary */}
                  <Card className="bg-gradient-to-br from-primary-50 to-purple-50 border-primary-200 mb-6">
                    <div className="space-y-2">
                      <div className="flex justify-between text-text-secondary">
                        <span>Subtotal (excl. GST):</span>
                        <span>
                          MVR{" "}
                          {calculateBasePrice(
                            selectedOrder.totalAmount
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-text-secondary">
                        <span>GST (8%):</span>
                        <span>
                          MVR{" "}
                          {calculateGST(selectedOrder.totalAmount).toFixed(2)}
                        </span>
                      </div>
                      <div className="h-px bg-primary-200 my-2"></div>
                      <div className="flex justify-between text-xl font-bold text-primary-600">
                        <span>Total (incl. GST):</span>
                        <span>MVR {selectedOrder.totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </Card>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="info"
                      onClick={startEditing}
                      leftIcon={<PencilIcon className="h-5 w-5" />}
                    >
                      Edit Order
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        printBill({
                          ...selectedOrder,
                          orderId: selectedOrder.id,
                          subtotal: calculateBasePrice(
                            selectedOrder.totalAmount
                          ),
                          gst: calculateGST(selectedOrder.totalAmount),
                          totalWithGST: selectedOrder.totalAmount,
                          confirmedBy: "Preview",
                        })
                      }
                      leftIcon={<PrinterIcon className="h-5 w-5" />}
                    >
                      Print Bill
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => initiatePayment(selectedOrder)}
                      leftIcon={<CheckCircleIcon className="h-5 w-5" />}
                    >
                      Confirm Payment
                    </Button>
                    <Button
                      variant="warning"
                      onClick={() => returnOrderToUser(selectedOrder.id)}
                      leftIcon={<ArrowPathIcon className="h-5 w-5" />}
                    >
                      Return to User
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => deleteOrder(selectedOrder.id)}
                      leftIcon={<TrashIcon className="h-5 w-5" />}
                      className="col-span-2"
                    >
                      Delete Order
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Edit Mode */}
                  <div className="mb-6">
                    <Button
                      variant={showProductSelector ? "outline" : "primary"}
                      onClick={() =>
                        setShowProductSelector(!showProductSelector)
                      }
                      leftIcon={
                        showProductSelector ? (
                          <XMarkIcon className="h-5 w-5" />
                        ) : (
                          <PlusIcon className="h-5 w-5" />
                        )
                      }
                      fullWidth
                    >
                      {showProductSelector
                        ? "Hide Products"
                        : "Add More Products"}
                    </Button>
                  </div>

                  {/* Product Selector in Edit Mode */}
                  {showProductSelector && (
                    <Card className="mb-6 bg-neutral-50">
                      <div className="mb-4">
                        <Input
                          placeholder="Search products..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          leftIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                        {filteredProducts.map((product) => (
                          <div
                            key={product.id}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-neutral-200"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-text-primary">
                                {product.name}
                              </p>
                              <p className="text-sm text-text-secondary">
                                MVR {product.price.toFixed(2)}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => addProductToEditedOrder(product)}
                            >
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* Edited Items List */}
                  <div className="mb-6">
                    <h3 className="font-bold text-text-primary mb-3">
                      Order Items
                    </h3>
                    {editedOrder.items.length === 0 ? (
                      <p className="text-center py-8 text-text-secondary">
                        No items in order
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {editedOrder.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-text-primary">
                                {item.name}
                              </p>
                              <p className="text-sm text-text-secondary">
                                MVR {item.price.toFixed(2)} each
                              </p>
                            </div>

                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  updateEditedItemQuantity(
                                    item.id,
                                    item.quantity - 1
                                  )
                                }
                              >
                                -
                              </Button>
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) =>
                                  updateEditedItemQuantity(
                                    item.id,
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="w-16 px-2 py-1 text-center border border-neutral-300 rounded-md"
                                min="1"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  updateEditedItemQuantity(
                                    item.id,
                                    item.quantity + 1
                                  )
                                }
                              >
                                +
                              </Button>
                              <span className="text-lg font-bold text-primary-600 w-24 text-right">
                                MVR {(item.price * item.quantity).toFixed(2)}
                              </span>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => removeEditedItem(item.id)}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Updated Summary */}
                  <Card className="bg-gradient-to-br from-primary-50 to-purple-50 border-primary-200 mb-6">
                    <div className="space-y-2">
                      <div className="flex justify-between text-text-secondary">
                        <span>New Total:</span>
                        <span className="font-bold text-primary-600">
                          MVR{" "}
                          {calculateTotalWithGST(
                            calculateOrderTotal(editedOrder.items)
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </Card>

                  {/* Edit Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="success"
                      onClick={saveEditedOrder}
                      disabled={editedOrder.items.length === 0}
                      fullWidth
                      leftIcon={<CheckCircleIcon className="h-5 w-5" />}
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outline"
                      onClick={cancelEditing}
                      fullWidth
                      leftIcon={<XMarkIcon className="h-5 w-5" />}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-24 text-text-secondary">
              <ReceiptPercentIcon className="h-24 w-24 mx-auto mb-6 opacity-20" />
              <p className="text-lg">Select an order to view details</p>
              <p className="text-sm mt-2">
                Or create a new order to get started
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Start Session Modal */}
      <Modal
        isOpen={showStartSession}
        onClose={() => setShowStartSession(false)}
        title="Start New Session"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Opening Cash Amount (MVR)"
            type="number"
            step="0.01"
            min="0"
            value={openingCash}
            onChange={(e) => setOpeningCash(e.target.value)}
            placeholder="0.00"
            leftIcon={<CurrencyDollarIcon className="h-5 w-5" />}
          />

          <div className="bg-info-50 border border-info-200 rounded-lg p-4">
            <p className="text-sm text-info-800">
              💡 Enter the cash amount in your register at the start of your
              shift. This will be used to calculate the expected cash at end of
              day.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="primary"
              onClick={startSession}
              fullWidth
              leftIcon={<CheckCircleIcon className="h-5 w-5" />}
            >
              Start Session
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowStartSession(false);
                setOpeningCash("");
              }}
              fullWidth
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Payment Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Process Payment"
        size="lg"
      >
        {orderToConfirm && (
          <div className="space-y-6">
            {/* Order Summary */}
            <Card className="bg-primary-50 border-primary-200">
              <div className="text-center">
                <p className="text-sm text-text-secondary mb-2">Total Amount</p>
                <p className="text-4xl font-bold text-primary-600">
                  MVR {orderToConfirm.totalAmount.toFixed(2)}
                </p>
              </div>
            </Card>

            {/* Payment Type Selection */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Payment Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPaymentType("cash")}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    paymentType === "cash"
                      ? "border-primary-500 bg-primary-50"
                      : "border-neutral-200 hover:border-primary-300"
                  }`}
                >
                  <BanknotesIcon className="h-8 w-8 mx-auto mb-2 text-primary-600" />
                  <p className="font-medium text-text-primary">Cash</p>
                </button>
                <button
                  onClick={() => setPaymentType("bank")}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    paymentType === "bank"
                      ? "border-primary-500 bg-primary-50"
                      : "border-neutral-200 hover:border-primary-300"
                  }`}
                >
                  <CurrencyDollarIcon className="h-8 w-8 mx-auto mb-2 text-primary-600" />
                  <p className="font-medium text-text-primary">Bank Transfer</p>
                </button>
              </div>
            </div>

            {/* Cash Payment Input */}
            {paymentType === "cash" && (
              <div>
                <Input
                  label="Cash Received (MVR)"
                  type="number"
                  step="0.01"
                  min={orderToConfirm.totalAmount}
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  placeholder="0.00"
                  leftIcon={<BanknotesIcon className="h-5 w-5" />}
                />

                {cashReceived &&
                  parseFloat(cashReceived) >= orderToConfirm.totalAmount && (
                    <Card className="mt-4 bg-success-50 border-success-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-success-900">
                          Change:
                        </span>
                        <span className="text-2xl font-bold text-success-600">
                          MVR{" "}
                          {(
                            parseFloat(cashReceived) -
                            orderToConfirm.totalAmount
                          ).toFixed(2)}
                        </span>
                      </div>
                    </Card>
                  )}
              </div>
            )}

            {/* Bank Payment Info */}
            {paymentType === "bank" && paymentSettings.enabled && (
              <Card className="bg-info-50 border-info-200">
                <h3 className="font-medium text-info-900 mb-3">
                  Bank Transfer Details
                </h3>
                <div className="space-y-2 text-sm text-info-800">
                  {paymentSettings.accountNumber && (
                    <p>
                      <strong>Account:</strong> {paymentSettings.accountNumber}
                    </p>
                  )}
                  {paymentSettings.viber && (
                    <p>
                      <strong>Viber:</strong> {paymentSettings.viber}
                    </p>
                  )}
                </div>
              </Card>
            )}

            {/* Confirm Button */}
            <div className="flex gap-3">
              <Button
                variant="success"
                size="lg"
                onClick={confirmOrder}
                disabled={
                  paymentType === "cash" &&
                  (!cashReceived ||
                    parseFloat(cashReceived) < orderToConfirm.totalAmount)
                }
                fullWidth
                leftIcon={<CheckCircleSolid className="h-6 w-6" />}
              >
                Confirm Payment
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  setShowPaymentModal(false);
                  setCashReceived("");
                  setPaymentType("cash");
                }}
                fullWidth
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* End of Day Modal */}
      <Modal
        isOpen={showEndOfDay}
        onClose={closeEndOfDay}
        title="End of Day Report"
        size="xl"
      >
        <div className="space-y-6">
          {loadingSales ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-text-secondary">Loading sales data...</p>
            </div>
          ) : (
            <>
              {/* Sales Summary */}
              <Card className="bg-gradient-to-br from-primary-50 to-purple-50 border-primary-200">
                <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
                  <ChartBarIcon className="h-6 w-6 text-primary-600" />
                  Sales Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-text-secondary">Total Orders</p>
                    <p className="text-2xl font-bold text-primary-600">
                      {calculateDaySummary().totalOrders}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary">Total Items</p>
                    <p className="text-2xl font-bold text-primary-600">
                      {calculateDaySummary().totalItems}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary">Cash Sales</p>
                    <p className="text-2xl font-bold text-success-600">
                      MVR {calculateDaySummary().cashSales.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary">Bank Sales</p>
                    <p className="text-2xl font-bold text-info-600">
                      MVR {calculateDaySummary().bankSales.toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="h-px bg-primary-200 my-4"></div>

                <div className="space-y-2">
                  <div className="flex justify-between text-text-secondary">
                    <span>Subtotal:</span>
                    <span>MVR {calculateDaySummary().subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-text-secondary">
                    <span>GST (8%):</span>
                    <span>MVR {calculateDaySummary().gst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold text-primary-600">
                    <span>Total Sales:</span>
                    <span>
                      MVR {calculateDaySummary().totalSales.toFixed(2)}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Cash Denominations */}
              <Card>
                <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
                  <BanknotesIcon className="h-6 w-6 text-primary-600" />
                  Count Cash Denominations
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: "note1000", label: "1000 Notes", value: 1000 },
                    { key: "note500", label: "500 Notes", value: 500 },
                    { key: "note100", label: "100 Notes", value: 100 },
                    { key: "note50", label: "50 Notes", value: 50 },
                    { key: "note20", label: "20 Notes", value: 20 },
                    { key: "note10", label: "10 Notes", value: 10 },
                    { key: "note5", label: "5 Notes", value: 5 },
                  ].map(({ key, label, value }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        {label}:
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={denominations[key]}
                        onChange={(e) =>
                          handleDenominationChange(key, e.target.value)
                        }
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <p className="text-xs text-text-secondary mt-1">
                        MVR {(denominations[key] * value).toFixed(2)}
                      </p>
                    </div>
                  ))}

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Coins:
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={denominations.coins}
                      onChange={(e) => handleCoinsChange(e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="text-xs text-text-secondary mt-1">
                      MVR {parseFloat(denominations.coins || 0).toFixed(2)}
                    </p>
                  </div>
                </div>

                <Card className="mt-6 bg-neutral-50">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-medium text-text-primary">
                      Total Cash Counted:
                    </span>
                    <span className="text-3xl font-bold text-primary-600">
                      MVR {calculateTotalFromDenominations().toFixed(2)}
                    </span>
                  </div>
                </Card>
              </Card>

              {/* Cash Summary & Difference */}
              <Card className="bg-warning-50 border-warning-200">
                <h3 className="text-lg font-bold text-warning-900 mb-4">
                  Cash Reconciliation
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-text-secondary">
                    <span>Expected Cash (Opening + Cash Sales):</span>
                    <span className="font-bold">
                      MVR {calculateExpectedCash().toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-text-secondary">
                    <span>Actual Cash Counted:</span>
                    <span className="font-bold">
                      MVR {calculateTotalFromDenominations().toFixed(2)}
                    </span>
                  </div>
                  <div className="h-px bg-warning-200 my-2"></div>
                  <div
                    className={`flex justify-between text-xl font-bold ${
                      calculateTotalFromDenominations() -
                        calculateExpectedCash() >=
                      0
                        ? "text-success-600"
                        : "text-danger-600"
                    }`}
                  >
                    <span>Difference:</span>
                    <span>
                      {calculateTotalFromDenominations() -
                        calculateExpectedCash() >=
                      0
                        ? "+"
                        : ""}
                      MVR{" "}
                      {(
                        calculateTotalFromDenominations() -
                        calculateExpectedCash()
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={exportToCSV}
                  leftIcon={<DocumentTextIcon className="h-5 w-5" />}
                >
                  Export CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={printReport}
                  leftIcon={<PrinterIcon className="h-5 w-5" />}
                >
                  Print Report
                </Button>
                <div className="flex-1"></div>
                <Button variant="outline" onClick={closeEndOfDay}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={archiveDay}
                  leftIcon={<CheckCircleIcon className="h-5 w-5" />}
                >
                  Close Session & Archive
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Session Selector Modal */}
      <Modal
        isOpen={showSessionSelector}
        onClose={() => setShowSessionSelector(false)}
        title="Multiple Active Sessions Found"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <p className="text-sm text-warning-800">
              ⚠️ Multiple active sessions detected. Please select which session
              you want to close with "End of Day".
            </p>
          </div>

          <div className="space-y-3">
            {availableSessions.map((session, index) => (
              <button
                key={session.id}
                onClick={() => selectSessionForEndOfDay(session)}
                className="w-full p-4 text-left bg-white border-2 border-neutral-200 hover:border-primary-500 rounded-lg transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-primary-600" />
                    <span className="font-bold text-text-primary">
                      Session #{index + 1}
                    </span>
                  </div>
                  <Badge variant="success">Active</Badge>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-text-secondary">Started:</p>
                    <p className="font-medium text-text-primary">
                      {new Date(session.startTime).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-text-secondary">Started By:</p>
                    <p className="font-medium text-text-primary">
                      {session.startedBy || "Cashier"}
                    </p>
                  </div>
                  <div>
                    <p className="text-text-secondary">Opening Cash:</p>
                    <p className="font-medium text-text-primary">
                      MVR {session.openingCash.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-text-secondary">Date:</p>
                    <p className="font-medium text-text-primary">
                      {session.date}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="bg-info-50 border border-info-200 rounded-lg p-4">
            <p className="text-sm text-info-800">
              💡 Tip: You can also close old sessions quickly from the
              "Sessions" page.
            </p>
          </div>
        </div>
      </Modal>

      {/* Keyboard Shortcuts Help Modal */}
      <Modal
        isOpen={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
        title="⌨️ Keyboard Shortcuts"
        size="lg"
      >
        <p className="text-text-secondary mb-6">
          Speed up your workflow with these shortcuts
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold text-text-primary mb-3">
              Session Management
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    Ctrl
                  </kbd>
                  <span className="text-text-secondary">+</span>
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    S
                  </kbd>
                </div>
                <span className="text-sm text-text-secondary">
                  Start Session
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    Ctrl
                  </kbd>
                  <span className="text-text-secondary">+</span>
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    E
                  </kbd>
                </div>
                <span className="text-sm text-text-secondary">End of Day</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-text-primary mb-3">Order Actions</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    Ctrl
                  </kbd>
                  <span className="text-text-secondary">+</span>
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    N
                  </kbd>
                </div>
                <span className="text-sm text-text-secondary">
                  Create New Order
                </span>
              </div>
              <div className="flex items-center justify-between">
                <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                  C
                </kbd>
                <span className="text-sm text-text-secondary">
                  Confirm Selected Order
                </span>
              </div>
              <div className="flex items-center justify-between">
                <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                  E
                </kbd>
                <span className="text-sm text-text-secondary">
                  Edit Selected Order
                </span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-text-primary mb-3">Navigation</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    ↑
                  </kbd>
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    ↓
                  </kbd>
                </div>
                <span className="text-sm text-text-secondary">
                  Navigate Orders
                </span>
              </div>
              <div className="flex items-center justify-between">
                <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                  Esc
                </kbd>
                <span className="text-sm text-text-secondary">
                  Close Modals/Cancel
                </span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-text-primary mb-3">Help</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    Ctrl
                  </kbd>
                  <span className="text-text-secondary">+</span>
                  <kbd className="px-2 py-1 bg-neutral-100 border border-neutral-300 rounded text-sm font-mono">
                    /
                  </kbd>
                </div>
                <span className="text-sm text-text-secondary">
                  Show This Help
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-2">
          <div className="bg-info-50 border border-info-200 rounded-lg p-3">
            <p className="text-sm text-info-800">
              💡 Shortcuts work when not typing in input fields
            </p>
          </div>
          <div className="bg-info-50 border border-info-200 rounded-lg p-3">
            <p className="text-sm text-info-800">
              🍎 On Mac, use{" "}
              <kbd className="px-2 py-1 bg-white border border-info-300 rounded text-xs font-mono">
                Cmd
              </kbd>{" "}
              instead of{" "}
              <kbd className="px-2 py-1 bg-white border border-info-300 rounded text-xs font-mono">
                Ctrl
              </kbd>
            </p>
          </div>
        </div>
      </Modal>

      {/* Payment Info Modal */}
      <Modal
        isOpen={showPaymentInfoModal}
        onClose={() => setShowPaymentInfoModal(false)}
        title="Payment Information"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-info-50 border border-info-200 rounded-lg p-4">
            <p className="text-sm text-info-800 mb-3">
              💳 Share these payment details with customers for bank transfers
            </p>
          </div>

          {paymentSettings.accountNumber && (
            <Card className="bg-neutral-50">
              <div className="flex items-center gap-3 mb-2">
                <BanknotesIcon className="h-6 w-6 text-primary-600" />
                <p className="text-sm font-medium text-text-secondary">Account Number</p>
              </div>
              <p className="text-xl font-bold text-text-primary">
                {paymentSettings.accountNumber}
              </p>
            </Card>
          )}

          {paymentSettings.viber && (
            <Card className="bg-neutral-50">
              <div className="flex items-center gap-3 mb-2">
                <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
                <p className="text-sm font-medium text-text-secondary">Viber</p>
              </div>
              <p className="text-xl font-bold text-text-primary">
                {paymentSettings.viber}
              </p>
            </Card>
          )}

          {paymentSettings.qrCodeUrl && (
            <Card className="bg-neutral-50">
              <p className="text-sm font-medium text-text-secondary mb-3 text-center">
                QR Code for Payment
              </p>
              <div className="flex justify-center">
                <img
                  src={paymentSettings.qrCodeUrl}
                  alt="Payment QR Code"
                  className="max-w-xs w-full rounded-lg shadow-md"
                />
              </div>
            </Card>
          )}

          {!paymentSettings.accountNumber && !paymentSettings.viber && !paymentSettings.qrCodeUrl && (
            <div className="text-center py-8">
              <p className="text-text-secondary">No payment information configured</p>
              <p className="text-sm text-text-muted mt-2">
                Contact admin to set up payment details
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CashierView;
